import React, { useState, useRef } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Input from '@mui/joy/Input';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Dropdown from '@mui/joy/Dropdown';
import MenuButton from '@mui/joy/MenuButton';
import Menu from '@mui/joy/Menu';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckIcon from '@mui/icons-material/Check';

import { themeColors, shadows } from '../../styles/theme';

const predefinedColors = [
  '#2196F3', '#1976D2', '#0D47A1', '#FF5722', '#E91E63', '#9C27B0',
  '#673AB7', '#3F51B5', '#03A9F4', '#00BCD4', '#009688', '#4CAF50',
  '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#795548'
];

export default function ColorPicker({ value, onChange, label = 'Color', required = false, helperText = '' }) {
  const [customColor, setCustomColor] = useState(value);
  const colorInputRef = useRef(null);

  const handleColorClick = (color) => {
    onChange(color);
    setCustomColor(color);
  };

  const handleCustomColorChange = (e) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    onChange(newColor);
  };

  return (
    <FormControl required={required}>
      <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
        {label}
        {required && (
          <Typography component="span" sx={{ color: themeColors.danger, ml: 0.5 }}>
            *
          </Typography>
        )}
      </FormLabel>
      <Stack direction="row" spacing={2} alignItems="center">
        <Dropdown>
          <MenuButton
            startDecorator={
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1.5,
                  background: `linear-gradient(135deg, ${value}, ${value}DD)`,
                  boxShadow: `0 2px 8px ${value}40`,
                }}
              />
            }
            endDecorator={<PaletteIcon />}
            sx={{
              minWidth: 180,
              border: 'none',
              boxShadow: shadows.sm,
              borderRadius: 2,
              backgroundColor: 'white',
              fontFamily: 'monospace',
              fontWeight: 600,
              '&:hover': {
                boxShadow: shadows.md,
                backgroundColor: themeColors.neutral[50],
              },
              transition: 'all 0.2s ease',
            }}
          >
            {value}
          </MenuButton>
          <Menu
            sx={{
              p: 2.5,
              maxWidth: 300,
              zIndex: 1400,
              border: 'none',
              boxShadow: shadows.xl,
              borderRadius: 2.5,
            }}
          >
            <Typography level="title-sm" sx={{ mb: 2, fontWeight: 600, color: themeColors.neutral[800] }}>
              Choose a color
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: 1.5,
                mb: 2.5
              }}
            >
              {predefinedColors.map((color) => (
                <Button
                  key={color}
                  onClick={() => handleColorClick(color)}
                  sx={{
                    minWidth: 36,
                    height: 36,
                    p: 0,
                    background: `linear-gradient(135deg, ${color}, ${color}DD)`,
                    border: 'none',
                    boxShadow: value === color ? `0 0 0 3px ${color}40, ${shadows.md}` : shadows.sm,
                    position: 'relative',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${color}, ${color}DD)`,
                      transform: 'scale(1.15)',
                      boxShadow: `0 0 0 3px ${color}40, ${shadows.lg}`,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {value === color && (
                    <CheckIcon sx={{ color: 'white', fontSize: 18, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
                  )}
                </Button>
              ))}
            </Box>
            <Box
              sx={{
                p: 2,
                backgroundColor: themeColors.neutral[50],
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600] }}>
                Use custom color picker below →
              </Typography>
            </Box>
          </Menu>
        </Dropdown>

        <Input
          type="color"
          value={customColor}
          onChange={handleCustomColorChange}
          ref={colorInputRef}
          sx={{
            width: 60,
            height: 56,
            p: 0.5,
            cursor: 'pointer',
            border: 'none',
            boxShadow: shadows.sm,
            borderRadius: 2,
            overflow: 'hidden',
            '&:hover': {
              boxShadow: shadows.md,
            },
            transition: 'all 0.2s ease',
            '& input': {
              cursor: 'pointer',
              border: 'none',
              height: '100%',
            }
          }}
        />
      </Stack>
      {helperText && (
        <FormHelperText sx={{ color: themeColors.neutral[600], fontSize: '0.875rem', mt: 0.75 }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}